import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { FormikProps } from "formik";
import { useEffect, useState } from "react";
import CustomDatePicker from "../../../../../../../../../components/CustomDatePicker";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import Input from "../../../../../../../../../components/Input";
import MicroTextArea from "../../../../../../../../../components/MicroTextArea";
import Text from "../../../../../../../../../components/Text";
import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { useQueryParams } from "../../../../../../../../../hooks/use-query-param";
import { useReduceDisclosure } from "../../../../../../../../../hooks/use-reduce-disclosure";
import { useRosPeTemplateArchive } from "../../../../../../../../../queries/encounter/use-ros-pe-template-archive";
import { useRosPeTemplateList } from "../../../../../../../../../queries/encounter/use-ros-pe-template-list";
import { useGetOrderPrescription } from "../../../../../../../../../queries/patient-chart/order/prescription/get-all-prescription";
import { useArchivePrescription } from "../../../../../../../../../queries/patient-chart/order/prescription/use-prescription-archive";
import { useArchiveOrder } from "../../../../../../../../../queries/patient-chart/order/use-archive-order";
import { useGetOrders } from "../../../../../../../../../queries/patient-chart/order/use-get-orders";
import { useGetAppointment } from "../../../../../../../../../queries/use-get-appointment";
import { NonNullableDate } from "../../../../../../../../../utils/date-time-utility";
import {
  DEFAULT_UUID_NAME_LIST,
  DEFAULT_VALUE_LABEL_LIST,
  DEF_PAGINATION,
  getXtenantId,
  transformMultiSelectFrontEndVal,
} from "../../../../../../../../../utils/utils";
import PeImportDrawer from "../../../../../../scheduling/appointment/encounter/default-forms/PeImportDrawer";
import RosImportDrawer from "../../../../../../scheduling/appointment/encounter/default-forms/RosImportDrawer";
import UpsertLabRequisitionForm from "../../../../../../scheduling/appointment/encounter/default-forms/UpsertLabRequisitionForm";
import { StyleAddButton } from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import UpsertPrescriptionForm from "../../../../../../scheduling/appointment/encounter/prescription/UpsertPrescriptionForm";
import PrintOrders from "../../../order/orders/PrintOrders";
import { getTransPayload } from "../../../order/prescription/OrderPrescriptionTableNew";
import PrintPrescription from "../../../order/prescription/PrintPrescription";
import ClinicalActions from "../components/form-components/ClinicalActions";
import ClinicalPreview from "../components/form-components/ClinicalPreview";
import CptCodesField from "../components/form-components/CptCodesField";
import IcdCodesField from "../components/form-components/IcdCodesField";
import { PrescriptionPreview } from "../components/form-components/PsychiatricNotePlan";
import ReferringOutPatientWrapper from "../components/form-components/ReferringOutPatientWrapper";
import PreviewWrapper from "../components/preview/previewWrapper";
import PsychoeducationTherapyForm from "./PsychoeducationTherapyForm";

interface PreOperativeNoteProps {
  formik: FormikProps<Form2Payload>;
}

const PreOperativeNote = ({ formik }: PreOperativeNoteProps) => {
  const [prefillData, setPrefillData] = useState<any>(undefined);
  const appointmentid = useQueryParams("appointmentid");
  const { data: appointmentData } = useGetAppointment({
    appointmentUuid: appointmentid ?? undefined,
    providerGroupId: getXtenantId() ?? "",
  });
  useEffect(() => {
    if (appointmentData?.data) {
      formik.setFieldValue(
        "attendingPhysician",
        appointmentData?.data?.providerName
      );
    }
  }, [appointmentData?.data]);

  const [
    rosTemplateDisclosure,
    templateDisclosure,
    ordersDisclosure,
    prescriptionDisclosure,
    referralDisclosure,
    printViewDisclosure,
    printOrderDisclosure,
  ] = useReduceDisclosure(7);

  const { data: allPrescription, refetch: refetchPrescription } =
    useGetOrderPrescription({
      pagination: DEF_PAGINATION,
      // patientUuid: patientid as any,
      appointmentUuid: appointmentid ?? "",
      archive: false,
      xTENANTID: getXtenantId(),
    });

  const { data: orders, refetch: refetchOrders } = useGetOrders({
    // patientUuid: patientid ?? "",
    appointmentUuid: appointmentid ?? "",
    archive: false,
    pagination: DEF_PAGINATION,
    xTENANTID: getXtenantId(),
  });

  const { mutateAsync: archivePrescription } = useArchivePrescription();
  async function archivePrescriptionHandler<T>(value: boolean, id: T) {
    return archivePrescription({
      prescriptionFormId: id?.toString() ?? "",
      status: value,
    }).then((res) => {
      refetchPrescription();
      return res;
    });
  }

  const { mutateAsync: archiveOrder } = useArchiveOrder();

  function orderArchiveHandler<T>(value: boolean, id: T) {
    return archiveOrder({
      patientOrderId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      refetchOrders();
      return res;
    });
  }

  const { data: peTemplates, refetch: refetchPeTemplates } =
    useRosPeTemplateList({
      pagination: {
        page: 0,
        pageSize: 50,
      },
      gridSorting: [],
      archive: false,
      status: true,
      templateType: "PHYSICAL_EXAM",
      appointmentUuid: appointmentid ?? undefined,
      xTENANTID: getXtenantId(),
    });

  const { data: rosTemplates, refetch: refetchRosTemplates } =
    useRosPeTemplateList({
      pagination: { page: 0, pageSize: 50 },
      gridSorting: [],
      archive: false,
      status: true,
      templateType: "ROS",
      appointmentUuid: appointmentid ?? undefined,
      xTENANTID: getXtenantId(),
    });

  const filteredPeTemplates = peTemplates?.data?.content?.filter(
    (d: any) => !!d?.description?.toString().trim()
  );
  const filteredRosTemplates = rosTemplates?.data?.content?.filter(
    (d: any) => !!d?.description?.toString().trim()
  );

  const { mutateAsync: archiveTemplate } = useRosPeTemplateArchive();

  const peRosTemplateArchive = <T,>(
    value: boolean,
    id: T,
    type: "PE" | "ROS"
  ) => {
    return archiveTemplate({
      encounterTemplateId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      if (type === "PE") refetchPeTemplates();
      else refetchRosTemplates();
      return res;
    });
  };

  return (
    <Flex p={1.5}>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle>Diagnosis</CustomTitle>
          <Flex flexDirection="row" gap={2} flexWrap="wrap" mt={1}>
            <IcdCodesField formik={formik} label="Add Diagnosis" />
            <CptCodesField formik={formik} />
          </Flex>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 4,
          }}
        >
          <CustomDatePicker
            label="Date of Procedure"
            name="dateOfProcedure"
            value={formik.values.dateOfProcedure}
            onChange={(val) => formik.setFieldValue("dateOfProcedure", val)}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 4,
          }}
        >
          <Input
            textFieldProps={{
              label: "Name of Consultant",
              name: "nameOfConsultant",
              placeholder: "Enter Name of Consultant",
              onBlur: formik.handleBlur,
              value: formik.values?.nameOfConsultant,
              onChange: formik.handleChange,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 4,
          }}
        >
          <Input
            textFieldProps={{
              label: "Attending Physician",
              disabled: true,
              name: "nameOfConsultant",
              placeholder: "Enter Attending Physician",
              onBlur: formik.handleBlur,
              value: formik.values?.attendingPhysician,
              onChange: formik.handleChange,
            }}
          />
        </Grid>
        <Grid size={12}>
          <CustomTextArea
            label="History of Present Illness"
            rows={3}
            name="historyOfPresentIllness"
            placeholder="Enter History of Present Illness"
            onBlur={formik.handleBlur}
            value={formik.values?.historyOfPresentIllness}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={12}>
          <Flex flexDirection="column" gap={1}>
            <MicroTextArea
              label="Allergies Free Note"
              rows={4}
              variant="outlined"
              name="allergyFreeText"
              placeholder="Enter Allergies, ex:dust <press enter> food <press enter> animal dander ...etc"
              onBlur={formik?.handleBlur}
              value={formik?.values?.allergyFreeText ?? ""}
              onChange={formik?.handleChange}
            />
          </Flex>

          {/* preview allergies, pmh, psh, fh, sh, current meds */}
          <ClinicalPreview />

          {/* add allergy, pmh, psh, fh, sh, current meds */}
          <ClinicalActions />
        </Grid>

        <Grid size={12}>
          <Flex flexDirection={"column"} width={"100%"} gap={1}>
            {!!filteredPeTemplates?.length && (
              <PreviewWrapper
                dataList={filteredPeTemplates}
                k="peTemplate"
                title="PE Templates"
                renderContent={(peTemplate: any) => (
                  <Text sx={{ color: "#424242" }}>
                    {`${peTemplate?.templateNameType ?? "-"} : ${
                      peTemplate?.description ?? "-"
                    }`}
                  </Text>
                )}
                onDelete={(uuid) => {
                  peRosTemplateArchive(true, uuid, "PE").then(() => {});
                }}
                onEdit={(peTemplate) => {
                  templateDisclosure.open(peTemplate);
                }}
              />
            )}
            {!!filteredRosTemplates?.length && (
              <PreviewWrapper
                dataList={filteredRosTemplates}
                k="rosTemplate"
                title="ROS Templates"
                renderContent={(rosTemplate: any) => (
                  <Text sx={{ color: "#424242" }}>
                    {`${rosTemplate?.templateNameType ?? "-"} : ${
                      rosTemplate?.description ?? "-"
                    }`}
                  </Text>
                )}
                onDelete={(uuid) => peRosTemplateArchive(true, uuid, "ROS")}
                onEdit={(rosTemplate) => {
                  templateDisclosure.open(rosTemplate);
                }}
              />
            )}
          </Flex>

          <Flex gap={1}>
            {(rosTemplates?.data?.content?.length ?? 0) < 1 && (
              <StyleAddButton
                w="100px"
                onClick={() => rosTemplateDisclosure.open()}
                label="Add ROS"
              />
            )}
            {(peTemplates?.data?.content?.length ?? 0) < 1 && (
              <StyleAddButton
                w="80px"
                onClick={() => templateDisclosure.open()}
                label="Add PE"
              />
            )}
          </Flex>

          <PeImportDrawer disclosureHost={templateDisclosure} />
          <RosImportDrawer disclosureHost={rosTemplateDisclosure} />
        </Grid>
        <Grid size={12}>
          <MicroTextArea
            label={"Assessment"}
            rows={4}
            variant="outlined"
            name="assessmentFreeNote"
            placeholder="Enter Assessments..."
            onBlur={formik.handleBlur}
            value={formik.values?.assessmentFreeNote || ""}
            onChange={formik.handleChange}
          />
        </Grid>

        <Grid size={12}>
          <Flex flexDirection={"column"} width={"100%"} gap={1}>
            {!!orders?.data?.content?.length && (
              <PreviewWrapper
                dataList={orders?.data?.content}
                k="order"
                title="Orders"
                renderContent={(it) => (
                  <Flex flexDirection={"column"} gap={1}>
                    <Text>{`${it?.orderTitle || ""}, ${
                      it?.orderType ?? ""
                    } - ${NonNullableDate(it?.appointmentDate)} - ${
                      it?.providerName
                    } `}</Text>
                    <Flex flexDirection={"column"} gap={0.5}>
                      <Text fontWeight={"bold"}>Tests:</Text>
                      {it?.tests
                        ? Object.values(it?.tests ?? {}).map(
                            (it: any, ind: number) => {
                              return (
                                <Flex gap={0.5} key={it.uuid}>
                                  <Text>{ind + 1}.</Text>
                                  <Text>{it}</Text>
                                </Flex>
                              );
                            }
                          )
                        : ""}
                    </Flex>
                  </Flex>
                )}
                onPrint={(uuid) => {
                  printOrderDisclosure.open(uuid);
                }}
                onDelete={(uuid) =>
                  orderArchiveHandler(true, uuid).then(() => {})
                }
                onEdit={(order) => {
                  const transformedData = {
                    ...order,
                    location: transformMultiSelectFrontEndVal(
                      DEFAULT_UUID_NAME_LIST,
                      order?.location
                    ),
                    labs: transformMultiSelectFrontEndVal(
                      DEFAULT_VALUE_LABEL_LIST,
                      order?.labs
                    ),
                    tests: transformMultiSelectFrontEndVal(
                      DEFAULT_UUID_NAME_LIST,
                      order?.tests
                    ),
                    medicalCodes: transformMultiSelectFrontEndVal(
                      DEFAULT_UUID_NAME_LIST,
                      order?.medicalCodes
                    ),
                    imagingCenter: order?.imagingCenter ?? undefined,
                    primaryProvider: order?.primaryProvider
                      ? {
                          value: Object.keys(order.primaryProvider)[0],
                          label: Object.values(order.primaryProvider)[0],
                        }
                      : undefined,
                    orderingProvider: order?.orderingProvider
                      ? {
                          value: Object.keys(order.orderingProvider)[0],
                          label: Object.values(order.orderingProvider)[0],
                        }
                      : undefined,
                    patientInstructions: order?.patientInstruction ?? undefined,
                  };

                  ordersDisclosure.open(transformedData);
                }}
              />
            )}
          </Flex>

          {!!allPrescription?.data?.content?.length && (
            <PrescriptionPreview
              data={allPrescription?.data?.content}
              onDelete={(uuid) => archivePrescriptionHandler(true, uuid)}
              onEdit={(prescription) => {
                setPrefillData(getTransPayload(prescription));
                prescriptionDisclosure.open(prescription.uuid);
              }}
            />
          )}

          {/* {!!allPrescription?.data?.content?.length && (
            <PreviewWrapper
              dataList={allPrescription?.data?.content}
              k="prescription"
              title="Rx/OTC"
              renderContent={(prescription: any) => (
                <Text sx={{ color: "#424242" }}>
                  {`medication: ${getValueFromObj(
                    prescription?.medicationId
                  )}- start: ${NonNullableDate(
                    prescription?.startDate
                  )}- duration: ${prescription?.duration}- qty: ${
                    prescription?.quantity
                  }- refills: ${prescription?.refills}` || "-"}
                </Text>
              )}
              onPrint={(uuid) => {
                printViewDisclosure.open(uuid);
              }}
              onDelete={(uuid) => archivePrescriptionHandler(true, uuid)}
              onEdit={(prescriptionItem) => {
                const transformedPayload = getTransPayload(prescriptionItem);
                setPrefillData(transformedPayload);
                prescriptionDisclosure.open();
              }}
            />
          )} */}

          {/* Action Buttons */}
          <Flex gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
            {/* <PrescriptionsPopUp
              buttonType="rxButton"
              variant="outlined"
              title="eRx"
              onOptionClick={(v: string) => {
                if (v === "paper_prescription") {
                  prescriptionDisclosure.open();
                } else if (v === "eletronic_prescription") {
                  handleElectronicPrescription();
                }
              }}
            /> */}

            <UpsertLabRequisitionForm disclosureHost={ordersDisclosure} />
            <StyleAddButton
              w="80px"
              onClick={() => ordersDisclosure.open()}
              label="Orders"
            />

            <StyleAddButton w="80px" onClick={() => {}} label="Sleep" />
            <StyleAddButton
              w="100px"
              onClick={() => prescriptionDisclosure.open()}
              label="Rx/OTC"
            />
            <UpsertPrescriptionForm
              prefillData={prefillData}
              setPrefillData={setPrefillData}
              disclosureHost={prescriptionDisclosure}
            />
            {/* <StyleAddButton w="80px" onClick={() => {}} label="Controlled" /> */}
            {/* <StyleAddButton w="80px" onClick={() => {}} label="Vaccination" /> */}

            <ReferringOutPatientWrapper
              referralDisclosure={referralDisclosure}
              referralDiagnosisCode={formik.values?.assessmentCodes?.map(
                (it: any) => ({ value: it.value, label: it.code })
              )}
            />
            <StyleAddButton
              w="120px"
              onClick={() => referralDisclosure.open()}
              label="Referral Out"
            />
          </Flex>
        </Grid>
        <Grid size={12}>
          <MicroTextArea
            label="Follow Up & Instructions"
            rows={4}
            variant="outlined"
            name="followUp"
            placeholder="Follow Up & Instructions..."
            onBlur={formik.handleBlur}
            value={formik.values?.followUp || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="psychoeducationSessionRequired"
                checked={!!formik.values?.psychoeducationSessionRequired}
                onChange={(e) =>
                  formik.setFieldValue(
                    "psychoeducationSessionRequired",
                    e.target.checked
                  )
                }
              />
            }
            label={
              <Text variant="body1" fontWeight={500}>
                Psychoeducation Session Required?
              </Text>
            }
          />
        </Grid>
        {formik.values?.psychoeducationSessionRequired && (
          <Grid size={12}>
            <PsychoeducationTherapyForm formik={formik} />
          </Grid>
        )}
      </Grid>
      {printViewDisclosure.data && (
        <PrintPrescription
          prescriptionFormId={printViewDisclosure.data}
          onClose={() => printViewDisclosure.close()}
        />
      )}
      <PrintOrders disclosureHost={printOrderDisclosure} />
    </Flex>
  );
};

export default PreOperativeNote;
